import React, { useEffect, useState, useRef } from "react";
import {
  formattedDate,
  formattedDuration,
  formattedPredictionDateTime,
} from "../../components/formatted/FormattedDateAndTime";
import {
  formattedStatus,
  toFrenchNumber,
} from "../../components/formatted/FormattedText";
import {
  hasOpeningHours,
  calculateHistoryDays,
  extractReliablePrediction,
} from "../../shared/utils/utils";
import Box from "@mui/material/Box";
import { Tooltip, Typography } from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import CustomPopover from "../../components/popover/CustomPopover";
import { CropImage } from "../../components/utils/ImageTreatment";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import i18n from "i18next";
import { formatMass } from "../../components/utils/formatMass.js";
import { isAdminMode } from "../../components/utils/isAdminMode.js";
import { MIN_HISTORY_DAYS_FOR_PREDICTION } from "../../constants/cache";
import MidCycleFluxChangeBadge from "./MidCycleFluxChangeBadge";

const ContainerTableView = ({
  data,
  weightKG,
  numberOfAnomaly,
  numberOfPackmat,
  durationFullFillingMs,
  fromDate,
  isLoadingHistoryCycleContainer,
  currentDate,
  noActivePackmat,
  fillingAlert,
  totalVolumeSortingError,
  totalWasteVolume,
  totalMassSortingError,
  actionButton,
}) => {
  const { t } = useTranslation();
  const [popoverContent, setPopoverContent] = useState(null);
  const [popoverLine, setPopoverLine] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const containersData = useSelector((state) => state.containers.data);
  const [dataContainer, setDataContainer] = useState({});
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [isHoveringImage, setIsHoveringImage] = useState(false);
  const imageContainerRef = useRef(null);
  const zoomModalRef = useRef(null);

  useEffect(() => {
    if (Object.keys(containersData).length !== 0) {
      if (data._id in containersData) {
        const containerActive = containersData[data._id];
        setDataContainer(containerActive);
      }
    }
  }, [containersData, data]);

  const handlePopoverOpen = (event, content, content2) => {
    setPopoverContent(content);
    setPopoverLine(content2);
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const getTimeString = () => {
    const time = new Date(data._time).toLocaleTimeString(
      i18n.language === "fr"
        ? "fr-FR"
        : i18n.language === "es"
          ? "es-ES"
          : i18n.language === "se"
            ? "sv-SE"
            : "en-GB",
      { hour: "2-digit", minute: "2-digit" },
    );
    return i18n.language === "fr" ? time.replace(":", "h") : time;
  };

  const formatDateForZoom = (dateString) => {
    const date = new Date(dateString);
    const language = i18n.language || "fr-FR";

    const day = date.getDate();
    const month = date.toLocaleDateString(language, { month: "long" });
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    if (language.startsWith("fr")) {
      return `${day} ${month} ${year}, ${hours}h${minutes}`;
    } else if (language.startsWith("en")) {
      const monthName = date.toLocaleDateString("en-US", { month: "long" });
      return `${monthName} ${day}, ${year}, ${hours}:${minutes}`;
    } else if (language.startsWith("es")) {
      const monthName = date.toLocaleDateString("es-ES", { month: "long" });
      return `${day} de ${monthName} de ${year}, ${hours}:${minutes}`;
    } else {
      return `${day} ${month} ${year}, ${hours}:${minutes}`;
    }
  };

  const handleImageClick = () => {
    if (data.image_url) {
      setIsImageZoomed(true);
    }
  };

  const handleCloseZoom = () => {
    setIsImageZoomed(false);
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isImageZoomed) {
        handleCloseZoom();
      }
    };

    const handleClickOutside = (e) => {
      if (
        isImageZoomed &&
        zoomModalRef.current &&
        !zoomModalRef.current.contains(e.target) &&
        !imageContainerRef.current?.contains(e.target)
      ) {
        handleCloseZoom();
      }
    };

    if (isImageZoomed) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isImageZoomed]);

  const getDateString = () => {
    const now = new Date();
    const dataDate = new Date(data._time);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dataDateOnly = new Date(
      dataDate.getFullYear(),
      dataDate.getMonth(),
      dataDate.getDate(),
    );

    if (dataDateOnly.getTime() === today.getTime()) {
      return t("Today");
    } else {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (dataDateOnly.getTime() === yesterday.getTime()) {
        return t("Yesterday");
      } else {
        return formattedDate(data._time, t);
      }
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <div className="data-table data-monitoring-releve">
          <div
            className="w-full"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            {data.image_url ? (
              <div
                ref={imageContainerRef}
                style={{
                  width: "112px",
                  height: "112px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "4px",
                  padding: "4px",
                  backgroundColor: "#FFFFFF",
                  overflow: "hidden",
                  flexShrink: 0,
                  cursor: "pointer",
                  position: "relative",
                }}
                onClick={handleImageClick}
                onMouseEnter={() => setIsHoveringImage(true)}
                onMouseLeave={() => setIsHoveringImage(false)}
              >
                <CropImage
                  container={data}
                  data={data}
                  imgDivWidth={104}
                  imgDivHeight={104}
                  isTooltip={true}
                  isMap={false}
                  isCharacterization={false}
                  displayLegend={false}
                  materialsDropdownToDisplay={[]}
                  showCheckbox={false}
                />
                {isHoveringImage && (
                  <>
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "white",
                        opacity: 0.2,
                        pointerEvents: "none",
                        zIndex: 9,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "45px",
                        height: "45px",
                        borderRadius: "30px",
                        backgroundColor: "grey",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                        zIndex: 10,
                      }}
                    >
                      <span
                        style={{
                          color: "white",
                          fontSize: "35px",
                          fontWeight: "normal",
                          lineHeight: 1,
                          marginTop: "-2px",
                        }}
                      >
                        +
                      </span>
                    </div>
                  </>
                )}
              </div>
            ) : null}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                gap: "4px",
                justifyContent: "center",
              }}
            >
              <span
                className="font-semibold truncate"
                style={{ fontSize: "14px", lineHeight: 1.25 }}
              >
                {data.wasteName || data.contentCategoryJoin?.label || "-"}
              </span>
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#6B7280",
                  lineHeight: 1.25,
                }}
              >
                {getDateString()} {t("at")} {getTimeString()}
              </span>
              <MidCycleFluxChangeBadge container={data} variant="table" />
            </div>
          </div>
        </div>

        <div
          className="data-table data-monitoring-address"
          style={{ overflow: "hidden" }}
        >
          <span
            className="truncate"
            style={{
              marginBottom: "2px",
              fontSize: "14px",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
            onMouseEnter={(event) =>
              handlePopoverOpen(
                event,
                data.addressName ? data.addressName : "-",
              )
            }
            onMouseLeave={handlePopoverClose}
          >
            {data.addressName || "-"}
          </span>
          {data?.positioning && data.positioning !== "" && (
            <Typography
              variant="caption"
              component="div"
              style={{
                fontWeight: "normal",
                fontSize: "0.75rem",
                letterSpacing: "-0.4px",
                lineHeight: "1.4",
                color: "#6B7280",
                maxHeight: "2.6em",
                overflow: "hidden",
                whiteSpace: "pre-line",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              {data.positioning.charAt(0).toUpperCase() +
                data.positioning.slice(1)}
            </Typography>
          )}
        </div>

        <div className="data-table data-monitoring-status">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            <span
              className="px-2 py-1 text-xs"
              style={{
                backgroundColor: "rgba(0, 167, 166, 0.125)",
                color: "#00A7A6",
                fontWeight: "bold",
                borderRadius: "999px",
                minWidth: "60px",
                textAlign: "center",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                justifyContent: "center",
              }}
              onMouseEnter={(event) =>
                handlePopoverOpen(event, formattedStatus(data.status))
              }
              onMouseLeave={handlePopoverClose}
            >
              {data.status === "drop_driver" && (
                <svg
                  width="11"
                  height="9"
                  viewBox="0 0 11 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.8 8.01667L0 4.21667L0.95 3.26667L3.8 6.11667L9.91667 0L10.8667 0.95L3.8 8.01667Z"
                    fill="#2AB9B8"
                  />
                </svg>
              )}
              {formattedStatus(data.status) || "-"}
            </span>
          </div>
        </div>

        {/* Erreur de tri - aligné à droite */}
        <div className="data-table data-monitoring-sorting-errors">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            {!isLoadingHistoryCycleContainer &&
            data.activatedSortingErrorOption ? (
              <>
                <span
                  style={{
                    marginBottom: "1px",
                    fontSize: "14px",
                    textAlign: "right",
                  }}
                >
                  {numberOfAnomaly}
                </span>
                <span style={{ fontSize: "0.75rem", color: "#6B7280" }}>
                  {totalWasteVolume > 0 ? (
                    <>
                      {Math.round(
                        (totalVolumeSortingError * 100) / totalWasteVolume,
                      )}
                      % {t("of undesirables")}
                      {isAdminMode() &&
                        totalMassSortingError != null &&
                        totalMassSortingError > 0 && (
                          <> - {formatMass(totalMassSortingError)}</>
                        )}
                    </>
                  ) : (
                    "-"
                  )}
                </span>
              </>
            ) : (
              <span
                style={{
                  fontSize: "14px",
                  textAlign: "right",
                  display: "block",
                  width: "100%",
                }}
              >
                -
              </span>
            )}
          </div>
        </div>

        {/* Compactage - aligné à droite */}
        {!noActivePackmat && (
          <div
            className="data-table data-monitoring-packmat"
            style={{ letterSpacing: "-0.4px" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              {data.packmatActive &&
                (!isLoadingHistoryCycleContainer ? (
                  <span style={{ fontSize: 14, textAlign: "right" }}>
                    {numberOfPackmat}
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: 14,
                      color: "var(--gainsboro)",
                      textAlign: "right",
                    }}
                  >
                    .
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* Action recommandée - aligné à droite */}
        <div
          className="data-table data-monitoring-action-recommended"
          style={{ overflow: "hidden" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {!isLoadingHistoryCycleContainer && fillingAlert ? (
              fillingAlert.rotationAlert ? (
                <span
                  style={{
                    fontSize: 14,
                    textAlign: "right",
                    fontWeight: "normal",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  {t("Rotation")}
                </span>
              ) : data.packmatActive &&
                (fillingAlert.packmatAlert ||
                  fillingAlert.packmatPossibility) ? (
                <span
                  style={{
                    fontSize: 14,
                    textAlign: "right",
                    fontWeight: "normal",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  {t("Compaction")}
                </span>
              ) : (
                <span
                  style={{
                    fontSize: 14,
                    textAlign: "right",
                    fontWeight: "normal",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  -
                </span>
              )
            ) : (
              <span
                style={{
                  fontSize: 14,
                  textAlign: "right",
                  fontWeight: "normal",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                  width: "100%",
                  minWidth: 0,
                }}
              >
                -
              </span>
            )}
          </div>
        </div>

        {/* Poids - aligné à droite */}
        <div
          className="data-table data-monitoring-weight"
          style={{ letterSpacing: "-0.4px" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            {!data.qualicheck && !data.characterization ? (
              weightKG != null ? (
                <span
                  style={{
                    fontSize: 14,
                    textAlign: "right",
                    fontWeight: "normal",
                    color: "#000000",
                  }}
                >
                  {toFrenchNumber(
                    weightKG < 1000 ? weightKG : weightKG / 1000,
                    1,
                  )}
                  {weightKG < 1000 ? "kg" : "t"}
                </span>
              ) : (
                <span
                  style={{
                    fontSize: 14,
                    textAlign: "right",
                    fontWeight: "normal",
                  }}
                >
                  -
                </span>
              )
            ) : (
              <span
                style={{
                  fontSize: 14,
                  textAlign: "right",
                  fontWeight: "normal",
                }}
              >
                -
              </span>
            )}
          </div>
        </div>

        {/* Remplissage avec Prévision max en dessous - aligné à droite */}
        <div
          className="data-table data-monitoring-filling"
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {data.activatedFillingLevelOption ? (
            <div
              className="data-row"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  textAlign: "right",
                  marginBottom: "2px",
                }}
              >
                {data.filling_level + "%"}
              </span>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#6B7280",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  minWidth: 0,
                  width: "100%",
                }}
              >
                {(() => {
                  const durationFullFillingMsToUse =
                    dataContainer?.durationFullFillingMs ||
                    durationFullFillingMs;
                  const durationData = durationFullFillingMsToUse?.[0];
                  // fromDate comes from the backend (historyFillingSpeed endpoint) and represents
                  // the start date of the history used to calculate filling speed.
                  // The backend may limit this history to a few months even if the container
                  // has existed for longer.
                  const fromDateToUse = dataContainer?.fromDate || fromDate;
                  const reliablePredictionValue =
                    extractReliablePrediction(dataContainer);
                  // Calculate history days with UTC normalization to avoid timezone issues
                  const historyDays = calculateHistoryDays(
                    fromDateToUse,
                    currentDate,
                  );
                  // Check if available history is sufficient (>= 30 days)
                  // Returns false if historyDays is null (invalid date or future date)
                  const hasEnoughHistory =
                    historyDays !== null &&
                    historyDays >= MIN_HISTORY_DAYS_FOR_PREDICTION;

                  if (durationData?.duration === -1) {
                    return (
                      <span style={{ color: "#DC2626" }}>
                        {t("Limit exceeded")}
                      </span>
                    );
                  }
                  if (data.filling_level === 100) {
                    return <span>{t("Maximum reached")}</span>;
                  }
                  if (
                    reliablePredictionValue === true &&
                    durationFullFillingMsToUse != null &&
                    hasOpeningHours(data?.addressJoin?.opening)
                  ) {
                    if (!hasEnoughHistory) {
                      return (
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100%",
                            display: "block",
                          }}
                          title={t("Not enough history for prediction")}
                        >
                          {t("Not enough history for prediction")}
                        </span>
                      );
                    }
                    if (durationData?.predictionDatetime) {
                      const timezone =
                        durationData?.timezone ||
                        data?.addressJoin?.timezone ||
                        "Europe/Paris";
                      const formatted = formattedPredictionDateTime(
                        durationData.predictionDatetime,
                        timezone,
                        t,
                      );
                      if (formatted) {
                        return <span>{formatted}</span>;
                      }
                    }
                    if (
                      durationData?.duration &&
                      Number.isFinite(durationData.duration)
                    ) {
                      return (
                        <span>
                          {formattedDuration(durationData.duration)
                            .split(",")
                            .map((part, index, array) => (
                              <React.Fragment key={index}>
                                {index > 0 && " "}
                                {part}
                                {index === 0 && array.length > 1 && ","}
                              </React.Fragment>
                            ))}
                        </span>
                      );
                    }
                  }
                  return null;
                })()}
              </div>
            </div>
          ) : (
            <div
              className="data-row"
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Tooltip title={t("filling_level_config_required")}>
                <InfoIcon style={{ color: "#888", cursor: "pointer" }} />
              </Tooltip>
            </div>
          )}
        </div>

        {/* Action - aligné au centre */}
        <div className="data-table data-monitoring-actions">{actionButton}</div>
      </div>
      <CustomPopover
        open={open}
        anchorEl={anchorEl}
        content={popoverContent}
        content2={popoverLine}
        onClose={handlePopoverClose}
      />

      {isImageZoomed && data.image_url && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            ref={zoomModalRef}
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "90vw",
              maxHeight: "90vh",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "16px",
                left: "24px",
                fontSize: "28px",
                color: "#222222",
                fontWeight: 500,
                zIndex: 10,
              }}
            >
              {formatDateForZoom(data._time)}
            </div>
            <button
              onClick={handleCloseZoom}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                width: "32px",
                height: "32px",
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <CloseIcon
                style={{
                  width: "24px",
                  height: "24px",
                  color: "#000000",
                }}
              />
            </button>
            <div
              style={{
                marginTop: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                maxWidth: "80vw",
                maxHeight: "80vh",
              }}
            >
              <CropImage
                container={data}
                data={data}
                containersSelected={[]}
                setContainersSelected={() => {}}
                imgDivWidth={Math.min(window.innerWidth * 0.7, 1200)}
                imgDivHeight={Math.min(window.innerHeight * 0.7, 800)}
                isTooltip={false}
                isMap={false}
                isCharacterization={false}
                displayLegend={false}
                materialsDropdownToDisplay={[]}
                showCheckbox={false}
                onClickZoomButton={() => {}}
                displayToBlock={false}
                setContainerActive={() => {}}
              />
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default ContainerTableView;
